import { useDataProvider, useGetIdentity } from "ra-core";
import { useCallback } from "react";

import type { Prospect } from "../types";

// CSV column headers map 1:1 to these keys (papaparse header mode).
// phone1/phone2 + email1/email2 collapse into the phones/emails jsonb arrays.
export type ProspectImportSchema = {
  owner_name: string;
  co_owner_name?: string;
  phone1?: string | number;
  phone2?: string | number;
  email1?: string;
  email2?: string;
  occupancy?: string;
  mailing_address?: string;
  mailing_city?: string;
  mailing_state?: string;
  mailing_zip?: string | number;
  property_address?: string;
  property_city?: string;
  property_state?: string;
  property_zip?: string | number;
  county?: string;
  parcel_apn?: string | number;
  property_type?: string;
  beds?: number;
  baths?: number;
  sqft?: number;
  year_built?: number;
  tax_value?: number;
  avm?: number;
  estimated_equity?: number;
  list_price?: number;
  listing_status?: string;
  auction_date?: string;
  auction_type?: string;
  opening_bid?: number;
  lien_amount?: number;
  case_number?: string;
  plaintiff_lender?: string;
  stage?: string;
  source?: string;
};

// papaparse dynamicTyping may yield numbers for digit-only cells; normalize to
// trimmed strings (and drop empties) for text columns.
const str = (v: unknown): string | undefined => {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s === "" ? undefined : s;
};

const num = (v: unknown): number | undefined => {
  if (v == null || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const buildProspectData = (
  row: ProspectImportSchema,
  salesId: unknown,
): Partial<Prospect> => {
  const phones = [
    { number: str(row.phone1), type: "Work" },
    { number: str(row.phone2), type: "Other" },
  ].filter((p): p is { number: string; type: string } => !!p.number);
  const emails = [
    { email: str(row.email1), type: "Work" },
    { email: str(row.email2), type: "Other" },
  ].filter((e): e is { email: string; type: string } => !!e.email);

  return {
    owner_name: str(row.owner_name) ?? "Unknown owner",
    co_owner_name: str(row.co_owner_name) ?? null,
    phones: phones as Prospect["phones"],
    emails: emails as Prospect["emails"],
    occupancy: str(row.occupancy) ?? null,
    mailing_address: str(row.mailing_address) ?? null,
    mailing_city: str(row.mailing_city) ?? null,
    mailing_state: str(row.mailing_state) ?? null,
    mailing_zip: str(row.mailing_zip) ?? null,
    property_address: str(row.property_address) ?? null,
    property_city: str(row.property_city) ?? null,
    property_state: str(row.property_state) ?? null,
    property_zip: str(row.property_zip) ?? null,
    county: str(row.county) ?? null,
    parcel_apn: str(row.parcel_apn) ?? null,
    property_type: str(row.property_type) ?? null,
    beds: num(row.beds) ?? null,
    baths: num(row.baths) ?? null,
    sqft: num(row.sqft) ?? null,
    year_built: num(row.year_built) ?? null,
    tax_value: num(row.tax_value) ?? null,
    avm: num(row.avm) ?? null,
    estimated_equity: num(row.estimated_equity) ?? null,
    list_price: num(row.list_price) ?? null,
    listing_status: str(row.listing_status) ?? null,
    auction_date: str(row.auction_date) ?? null,
    auction_type: str(row.auction_type) ?? null,
    opening_bid: num(row.opening_bid) ?? null,
    lien_amount: num(row.lien_amount) ?? null,
    case_number: str(row.case_number) ?? null,
    plaintiff_lender: str(row.plaintiff_lender) ?? null,
    stage: str(row.stage) ?? "Researching",
    source: str(row.source) ?? "CSV import",
    sales_id: salesId as Prospect["sales_id"],
  };
};

export function useProspectImport() {
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();

  return useCallback(
    async (batch: ProspectImportSchema[]) => {
      const salesId = identity?.id;

      // Dedup by APN against existing prospects (unique on sales_id + parcel_apn).
      const apns = [
        ...new Set(
          batch.map((r) => str(r.parcel_apn)).filter((a): a is string => !!a),
        ),
      ];
      const existingByApn = new Map<string, Prospect>();
      if (apns.length > 0) {
        const { data } = await dataProvider.getList<Prospect>("prospects", {
          filter: {
            "parcel_apn@in": `(${apns.map((a) => `"${a}"`).join(",")})`,
          },
          pagination: { page: 1, perPage: apns.length },
          sort: { field: "id", order: "ASC" },
        });
        for (const p of data) {
          if (p.parcel_apn) existingByApn.set(p.parcel_apn, p);
        }
      }

      await Promise.all(
        batch.map((row) => {
          const data = buildProspectData(row, salesId);
          const apn = str(row.parcel_apn);
          const existing = apn ? existingByApn.get(apn) : undefined;
          if (existing) {
            return dataProvider.update("prospects", {
              id: existing.id,
              data,
              previousData: existing,
            });
          }
          return dataProvider.create("prospects", { data });
        }),
      );
    },
    [dataProvider, identity?.id],
  );
}
